import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import { Toaster, DialogHost } from 'shared-ui';

// Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import SSO from './pages/Auth/SSO';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Wallet from './pages/Wallet';
import Messages from './pages/Messages';
import ConversationDetail from './pages/ConversationDetail';
import Subscriptions from './pages/Subscriptions';
import Tickets from './pages/Tickets';
import MyQR from './pages/MyQR';
import EventChat from './pages/EventChat';
import Wishlist from './pages/Wishlist';
import Businesses from './pages/Businesses';
import Marketplace from './pages/Marketplace';
import CreateListing from './pages/CreateListing';
import MyListings from './pages/MyListings';
import Groups from './pages/Groups';
import GroupJoinInvite from './pages/GroupJoinInvite';
import GroupProductDetail from './pages/GroupProductDetail';
import Events from './pages/Events';
import CreateEvent from './pages/CreateEvent';
import Connections from './pages/Connections';
import Finance from './pages/Finance';
import Documents from './pages/Documents';
import GiftRegistries from './pages/GiftRegistries';
import Calendar from './pages/Calendar';
import TaxAutomation from './pages/TaxAutomation';
import Portfolio from './pages/Portfolio';
import Vault from './pages/Vault';
import ActivityTracking from './pages/ActivityTracking';
import LocationSharing from './pages/LocationSharing';
import ImageSharing from './pages/ImageSharing';
import CollaborativeWork from './pages/CollaborativeWork';
import FamilyGroups from './pages/FamilyGroups';
import SharedLiving from './pages/SharedLiving';
import GroupPlanning from './pages/GroupPlanning';
import Healthcare from './pages/Healthcare';
import Education from './pages/Education';
import LicenseManagement from './pages/LicenseManagement';
import ArtSystem from './pages/ArtSystem';
import MoreFeatures from './pages/MoreFeatures';
import PersonalInventory from './pages/PersonalInventory';
import TrustScore from './pages/TrustScore';
import Blockchain from './pages/Blockchain';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Upgrade from './pages/Upgrade';
import Search from './pages/Search';
import Jobs from './pages/Jobs';
import Workplaces from './pages/Workplaces';
import StaffDashboard from './pages/StaffDashboard';
import BusinessCRM from './pages/BusinessCRM';
import InboxHub from './pages/InboxHub';
import SocialHub from './pages/SocialHub';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredTier?: string[] }> = ({ 
  children, 
  requiredTier 
}) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '32px'
          }}>
            🥭
          </div>
          <p style={{ color: 'white', fontSize: '18px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check tier requirements
  if (requiredTier && user && !requiredTier.includes(user.subscription)) {
    return <Navigate to="/upgrade" replace />;
  }

  return <>{children}</>;
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* SSO: business frontends send users here with ?return_url=...; we redirect back with #mango_token=... */}
      <Route path="/sso" element={<SSO />} />

      {/* Protected Routes - All Users */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <Layout><Orders /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/orders/:id" element={
        <ProtectedRoute>
          <Layout><OrderDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          <Layout><Wallet /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout><Messages /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages/:id" element={
        <ProtectedRoute>
          <Layout><ConversationDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/subscriptions" element={
        <ProtectedRoute>
          <Layout><Subscriptions /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/business-crm" element={
        <ProtectedRoute>
          <Layout><BusinessCRM /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/inbox" element={
        <ProtectedRoute>
          <Layout><InboxHub /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/social-hub" element={
        <ProtectedRoute>
          <Layout><SocialHub /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/tickets" element={
        <ProtectedRoute>
          <Layout><Tickets /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-qr" element={
        <ProtectedRoute>
          <Layout><MyQR /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/event-chat/:eventId" element={
        <ProtectedRoute>
          <Layout><EventChat /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Layout><Calendar /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/wishlist" element={
        <ProtectedRoute>
          <Layout><Wishlist /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/businesses" element={
        <ProtectedRoute>
          <Layout><Businesses /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/search" element={
        <ProtectedRoute>
          <Layout><Search /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs" element={
        <ProtectedRoute>
          <Layout><Jobs /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Workplaces - Staff access to businesses */}
      <Route path="/workplaces" element={
        <ProtectedRoute>
          <Workplaces />
        </ProtectedRoute>
      } />
      <Route path="/workplace/:businessId" element={
        <ProtectedRoute>
          <StaffDashboard />
        </ProtectedRoute>
      } />
      <Route path="/workplace/:businessId/*" element={
        <ProtectedRoute>
          <StaffDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><Settings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><Profile /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/upgrade" element={
        <ProtectedRoute>
          <Layout><Upgrade /></Layout>
        </ProtectedRoute>
      } />

      {/* Social Tier+ Routes */}
      <Route path="/marketplace" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><Marketplace /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/marketplace/create" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><CreateListing /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/marketplace/my-listings" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><MyListings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/groups/join-invite" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <GroupJoinInvite />
        </ProtectedRoute>
      } />
      <Route path="/groups" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><Groups /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/groups/:groupId/product/:productId" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><GroupProductDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><Events /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/events/create" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><CreateEvent /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/connections" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><Connections /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/gift-registries" element={
        <ProtectedRoute requiredTier={['social', 'pro', 'premium']}>
          <Layout><GiftRegistries /></Layout>
        </ProtectedRoute>
      } />

      {/* Pro Tier+ Routes */}
      <Route path="/finance" element={
        <ProtectedRoute requiredTier={['pro', 'premium']}>
          <Layout><Finance /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/tax" element={
        <ProtectedRoute requiredTier={['pro', 'premium']}>
          <Layout><TaxAutomation /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/portfolio" element={
        <ProtectedRoute requiredTier={['pro', 'premium']}>
          <Layout><Portfolio /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute requiredTier={['pro', 'premium']}>
          <Layout><Documents /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/vault" element={
        <ProtectedRoute requiredTier={['pro', 'premium']}>
          <Layout><Vault /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/activity" element={<ProtectedRoute><Layout><ActivityTracking /></Layout></ProtectedRoute>} />
      <Route path="/location-sharing" element={<ProtectedRoute><Layout><LocationSharing /></Layout></ProtectedRoute>} />
      <Route path="/image-sharing" element={<ProtectedRoute><Layout><ImageSharing /></Layout></ProtectedRoute>} />
      <Route path="/collaborative-work" element={<ProtectedRoute><Layout><CollaborativeWork /></Layout></ProtectedRoute>} />
      <Route path="/family-groups" element={<ProtectedRoute><Layout><FamilyGroups /></Layout></ProtectedRoute>} />
      <Route path="/shared-living" element={<ProtectedRoute><Layout><SharedLiving /></Layout></ProtectedRoute>} />
      <Route path="/group-planning" element={<ProtectedRoute><Layout><GroupPlanning /></Layout></ProtectedRoute>} />
      <Route path="/healthcare" element={<ProtectedRoute><Layout><Healthcare /></Layout></ProtectedRoute>} />
      <Route path="/education" element={<ProtectedRoute><Layout><Education /></Layout></ProtectedRoute>} />
      <Route path="/licenses" element={<ProtectedRoute><Layout><LicenseManagement /></Layout></ProtectedRoute>} />
      <Route path="/art" element={<ProtectedRoute><Layout><ArtSystem /></Layout></ProtectedRoute>} />
      <Route path="/personal-inventory" element={<ProtectedRoute><Layout><PersonalInventory /></Layout></ProtectedRoute>} />
      <Route path="/trust-score" element={<ProtectedRoute><Layout><TrustScore /></Layout></ProtectedRoute>} />
      <Route path="/blockchain" element={<ProtectedRoute><Layout><Blockchain /></Layout></ProtectedRoute>} />
      <Route path="/more" element={<ProtectedRoute><Layout><MoreFeatures /></Layout></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        {/* In-app replacements for alert/confirm/prompt - toasts + dialogs (bottom sheet on a phone). */}
        <Toaster />
        <DialogHost />
      </AuthProvider>
    </Router>
  );
};

export default App;
